import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { DEFAULT_DAILY_CHALLENGES, DEFAULT_WEEKLY_CHALLENGES, DEFAULT_MONTHLY_CHALLENGES, CHALLENGE_TYPES } from '../../../data/challengeDefinitions';
import { awardRewards, ACTIVITY_TYPES } from '../../../utils/gamificationManager';
import { useTCoins } from '../../../context/TCoinsContext';

const ChallengeDisplay = ({ userId, teamId }) => {
  const [dailyChallenges, setDailyChallenges] = useState([]);
  const [weeklyChallenges, setWeeklyChallenges] = useState([]);
  const [monthlyChallenges, setMonthlyChallenges] = useState([]);
  const [userProgress, setUserProgress] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    loadChallenges();
  }, [userId]);

  const loadChallenges = async () => {
    try {
      // Load user progress
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      let progressData = {
        daily: {},
        weekly: {},
        monthly: {}
      };

      if (userSnap.exists()) {
        const userData = userSnap.data();
        progressData = {
          daily: userData.dailyChallenges || {},
          weekly: userData.weeklyChallenges || {},
          monthly: userData.monthlyChallenges || {}
        };
      }

      setUserProgress(progressData);

      // Initialize challenges with progress
      const today = new Date().toISOString().split('T')[0];
      const thisWeek = getWeekString(new Date());
      const thisMonth = new Date().toISOString().slice(0, 7);

      const daily = DEFAULT_DAILY_CHALLENGES.map(challenge => ({
        ...challenge,
        progress: progressData.daily?.[today]?.progress?.[challenge.challengeId] || 0,
        completed: progressData.daily?.[today]?.completed?.includes(challenge.challengeId) || false
      }));

      const weekly = DEFAULT_WEEKLY_CHALLENGES.map(challenge => ({
        ...challenge,
        progress: progressData.weekly?.[thisWeek]?.progress?.[challenge.challengeId] || 0,
        completed: progressData.weekly?.[thisWeek]?.completed?.includes(challenge.challengeId) || false
      }));

      const monthly = DEFAULT_MONTHLY_CHALLENGES.map(challenge => ({
        ...challenge,
        progress: progressData.monthly?.[thisMonth]?.progress?.[challenge.challengeId] || 0,
        completed: progressData.monthly?.[thisMonth]?.completed?.includes(challenge.challengeId) || false
      }));

      setDailyChallenges(daily);
      setWeeklyChallenges(weekly);
      setMonthlyChallenges(monthly);
    } catch (error) {
      console.error('Error loading challenges:', error);
    } finally {
      setLoading(false);
    }
  };

  const getWeekString = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return `${d.getUTCFullYear()}-W${String(Math.ceil((((d - yearStart) / 86400000) + 1) / 7)).padStart(2, '0')}`;
  };

  const getProgressPercent = (challenge) => {
    const progress = challenge.progress || 0;
    const target = challenge.requirement.value;
    return Math.min((progress / target) * 100, 100);
  };

  const renderChallengeCard = (challenge) => {
    const progressPercent = getProgressPercent(challenge);
    const isCompleted = challenge.completed;

    return (
      <div
        key={challenge.challengeId}
        className={`border-2 rounded-lg p-4 ${
          isCompleted
            ? 'border-green-500 bg-green-50'
            : 'border-gray-200 bg-white'
        }`}
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <h5 className="font-semibold text-gray-800 mb-1">{challenge.nameThai}</h5>
            <p className="text-sm text-gray-600">{challenge.description}</p>
          </div>
          {isCompleted && (
            <span className="text-2xl">✅</span>
          )}
        </div>

        {!isCompleted && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
              <span>ความคืบหน้า</span>
              <span>{challenge.progress || 0}/{challenge.requirement.value}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all"
                style={{ width: `${progressPercent}%` }}
              ></div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between text-sm">
          <div className="text-gray-600">
            <span className="font-medium">รางวัล:</span>
            <span className="ml-2 text-blue-600">+{challenge.reward.points} Points</span>
            <span className="ml-2 text-green-600">+{challenge.reward.xp} XP</span>
            <span className="ml-2 text-yellow-600">+{challenge.reward.coins} Coins</span>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Daily Challenges */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-800">📅 Daily Challenges</h3>
          <span className="text-sm text-gray-600">
            Reset ทุกวันเวลา 00:00
          </span>
        </div>
        {dailyChallenges.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>ยังไม่มี Daily Challenges</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {dailyChallenges.map(renderChallengeCard)}
          </div>
        )}
      </div>

      {/* Weekly Challenges */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-800">📆 Weekly Challenges</h3>
          <span className="text-sm text-gray-600">
            Reset ทุกวันจันทร์
          </span>
        </div>
        {weeklyChallenges.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>ยังไม่มี Weekly Challenges</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {weeklyChallenges.map(renderChallengeCard)}
          </div>
        )}
      </div>

      {/* Monthly Challenges */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-800">📊 Monthly Challenges</h3>
          <span className="text-sm text-gray-600">
            Reset ทุกวันที่ 1 ของเดือน
          </span>
        </div>
        {monthlyChallenges.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>ยังไม่มี Monthly Challenges</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {monthlyChallenges.map(renderChallengeCard)}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChallengeDisplay;

